(() => {
  'use strict';

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const esc = (s = '') => String(s).replace(/[&<>'"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[c]));

  function parseJSON() {
    const node = qs('[data-masal-appointment-data]');
    if (!node) return { locations: [], config: {} };
    try { return JSON.parse(node.textContent); }
    catch (e) { console.warn('MASAL appointment data could not be parsed.', e); return { locations: [], config: {} }; }
  }

  const data = parseJSON();
  const locations = Array.isArray(data.locations) ? data.locations : [];
  const config = data.config || {};
  const params = new URLSearchParams(window.location.search);
  const requestedHandle = (params.get('store') || '').trim();
  const selected = requestedHandle ? locations.find(l => l.handle === requestedHandle) : null;



  /* ---------------------------------------------------------
     Non-blocking Bookeo loader — Bookeo-compatible version

     Bookeo's widget.js is sensitive to where/how it is inserted.
     The script must live inside the target mount and, when loaded
     dynamically, may need axiomct_onload() to be triggered.
     This mirrors the behaviour used by established Bookeo wrappers
     while keeping the rest of the MASAL page renderable first.
     --------------------------------------------------------- */
  function loadBookeoAsync() {
    const template = qs('[data-masal-bookeo-template]');
    const mount = qs('[data-masal-bookeo-mount]');
    if (!template || !mount || mount.dataset.bookeoStarted === 'true') return;

    mount.dataset.bookeoStarted = 'true';

    const fragment = template.content.cloneNode(true);
    const scripts = Array.from(fragment.querySelectorAll('script'));
    scripts.forEach(script => script.remove());

    // Preserve any non-script markup in the Bookeo snippet.
    mount.appendChild(fragment);

    const loader = qs('[data-masal-bookeo-loader]', mount);
    let finished = false;
    let watcher = null;

    const finish = () => {
      if (finished) return;
      finished = true;
      if (watcher) window.clearInterval(watcher);
      if (loader) loader.remove();
      mount.classList.add('is-loaded');
      mount.classList.remove('has-bookeo-error');
    };

    const showError = () => {
      if (finished) return;
      mount.classList.add('has-bookeo-error');
      if (loader) {
        loader.innerHTML = '<span>Appointment times could not load. Please refresh the page or try again.</span>';
      }
    };

    const widgetExists = () => {
      // Bookeo commonly creates axiomct_div and/or an iframe.
      const globalWidget = document.getElementById('axiomct_div');
      const iframe = mount.querySelector('iframe') || (globalWidget && globalWidget.querySelector('iframe'));
      return Boolean(iframe || globalWidget || mount.querySelector('[id^="axiomct_"]'));
    };

    const triggerBookeoIfNeeded = () => {
      try {
        const hasDiv = typeof window.axiomct_div !== 'undefined' && window.axiomct_div;
        const hasSpinner = typeof window.axiomct_spinner !== 'undefined' && window.axiomct_spinner;
        if (!hasDiv && !hasSpinner && typeof window.axiomct_onload === 'function') {
          window.axiomct_onload();
        }
      } catch (e) {
        console.warn('Bookeo manual start was not required or could not run.', e);
      }
    };

    const watchForWidget = () => {
      let checks = 0;
      watcher = window.setInterval(() => {
        checks += 1;
        if (widgetExists()) {
          finish();
          return;
        }
        // Bookeo may expose its init hook slightly after widget.js loads.
        if (checks === 5 || checks === 15) triggerBookeoIfNeeded();
        if (checks >= 120) { // ~12 seconds
          window.clearInterval(watcher);
          showError();
        }
      }, 100);
    };

    const runScript = (index) => {
      if (index >= scripts.length) {
        // Established dynamic Bookeo integrations explicitly call the
        // widget onload hook when the script did not auto-start.
        window.setTimeout(triggerBookeoIfNeeded, 250);
        window.setTimeout(triggerBookeoIfNeeded, 1000);
        watchForWidget();
        return;
      }

      const original = scripts[index];
      const script = document.createElement('script');
      Array.from(original.attributes).forEach(attr => script.setAttribute(attr.name, attr.value));

      if (original.src) {
        script.async = false;
        script.onload = () => runScript(index + 1);
        script.onerror = () => {
          console.error('Bookeo script failed to load:', original.src);
          showError();
        };

        // CRITICAL: append to the Bookeo mount, not document.body.
        // Bookeo uses its script position / globals to determine where
        // the widget should render.
        mount.appendChild(script);
      } else {
        script.textContent = original.textContent || '';
        mount.appendChild(script);
        runScript(index + 1);
      }
    };

    if (!scripts.length) {
      showError();
      return;
    }

    runScript(0);
  }

  function scheduleBookeoLoad() {
    // Keep the page non-blocking, but do not use defer/async on Bookeo's
    // own widget script. Bookeo's integration is sensitive to that.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => window.setTimeout(loadBookeoAsync, 0), { once: true });
    } else {
      window.setTimeout(loadBookeoAsync, 0);
    }
  }

  if (requestedHandle && !selected) {
    document.documentElement.dataset.masalAppointmentMode = 'default';
    const clean = new URL(window.location.href);
    clean.searchParams.delete('store');
    history.replaceState({}, '', clean.pathname + clean.search + clean.hash);
  }

  function fullAddress(l) {
    return [l.address1, l.address2, l.city, l.state, l.postcode, l.country].filter(Boolean).join(', ');
  }

  function directionsUrl(l) {
    const addr = fullAddress(l);
    return addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : '#';
  }

  function formatTimeCompact(value = '') {
    const text = String(value).trim().replace(/\s+/g, ' ');
    if (!text) return '';
    const m = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (!m) return text;
    const hour = String(Number(m[1]));
    const mins = m[2] || '00';
    const suffix = m[3].toLowerCase();
    return mins === '00' ? `${hour}${suffix}` : `${hour}:${mins}${suffix}`;
  }

  function compactOpeningHours(raw = '') {
    const source = String(raw || '').trim();
    if (!source) return '';

    const dayMap = {
      monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
      friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
    };
    const dayOrder = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    const pattern = /(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*:\s*([^\n]+?)(?=(?:\s*(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*:)|$)/gi;
    const entries = [];
    let match;
    while ((match = pattern.exec(source)) !== null) {
      const day = dayMap[match[1].toLowerCase()];
      let hours = match[2].trim();
      if (/^closed$/i.test(hours)) {
        hours = 'Closed';
      } else {
        const range = hours.match(/(\d{1,2}(?::\d{2})?\s*(?:AM|PM))\s*[–—-]\s*(\d{1,2}(?::\d{2})?\s*(?:AM|PM))/i);
        if (range) hours = `${formatTimeCompact(range[1])}–${formatTimeCompact(range[2])}`;
      }
      entries.push({ day, hours });
    }

    if (!entries.length) return source.replace(/\s*\n\s*/g, ' · ').replace(/\s{2,}/g, ' ');

    entries.sort((a,b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
    const groups = [];
    for (const entry of entries) {
      const last = groups[groups.length - 1];
      const currentIndex = dayOrder.indexOf(entry.day);
      if (last && last.hours === entry.hours && currentIndex === last.endIndex + 1) {
        last.end = entry.day;
        last.endIndex = currentIndex;
      } else {
        groups.push({ start: entry.day, end: entry.day, endIndex: currentIndex, hours: entry.hours });
      }
    }
    return groups.map(g => `${g.start === g.end ? g.start : `${g.start}–${g.end}`}: ${g.hours}`).join(' · ');
  }

  function bookingHref(l) {
    const type = String(l.bookingType || '').toLowerCase();
    if (type.includes('external') && l.bookingUrl) return l.bookingUrl;
    if (type.includes('masal')) return `${config.appointmentsPath || '/pages/appointments'}?store=${encodeURIComponent(l.handle)}`;
    if (l.website) return l.website;
    if (l.phone) return `tel:${String(l.phone).replace(/\s+/g, '')}`;
    return '#';
  }

  function populateText(attr, value) {
    qsa(`[${attr}]`).forEach(el => { el.textContent = value || ''; });
  }

  function populateSelectedStore(l) {
    if (!l) return;
    populateText('data-masal-store-name', l.name);
    populateText('data-masal-store-address', fullAddress(l));
    const phone = String(l.phone || '').trim();
    populateText('data-masal-store-phone-text', phone);
    populateText('data-masal-store-hours', compactOpeningHours(l.openingHours) || 'Contact the store for current opening hours.');
    populateText('data-masal-store-badge', (l.storeType || '').toLowerCase().includes('flagship') ? (config.flagshipBadge || 'Flagship Store') : (config.stockistBadge || 'Authorised MASAL Stockist'));
    populateText('data-masal-store-city', [l.city, l.state].filter(Boolean).join(', '));

    qsa('[data-masal-store-image]').forEach(el => {
      const fallback = el.dataset.fallback || '';
      el.style.backgroundImage = `url("${l.image || fallback}")`;
    });
    qsa('[data-masal-store-phone-row]').forEach(row => {
      row.hidden = !phone;
      row.style.display = phone ? '' : 'none';
    });
    qsa('[data-masal-store-phone]').forEach(el => {
      if (phone) {
        el.href = `tel:${phone.replace(/\s+/g, '')}`;
        el.hidden = false;
      } else {
        el.removeAttribute('href');
        el.hidden = true;
      }
    });
    qsa('[data-masal-store-website]').forEach(el => {
      if (l.website) { el.href = l.website; el.hidden = false; }
      else { el.hidden = true; }
    });
    qsa('[data-masal-store-directions]').forEach(el => {
      const href = directionsUrl(l);
      if (href !== '#') { el.href = href; el.hidden = false; }
      else { el.hidden = true; }
    });

    qsa('[data-masal-store-map]').forEach(el => {
      const lat = String(l.lat || '').trim();
      const lng = String(l.lng || '').trim();
      const query = lat && lng ? `${lat},${lng}` : fullAddress(l);
      if (query) {
        el.src = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
        el.title = `${l.name || 'MASAL store'} map`;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    const type = String(l.bookingType || '').trim().toLowerCase();
    const isMasalBooking = type.includes('masal');
    const isExternalBooking = type.includes('external');

    qsa('[data-masal-bookeo-wrap]').forEach(el => {
      el.hidden = !isMasalBooking;
      el.style.display = isMasalBooking ? '' : 'none';
    });
    qsa('[data-masal-external-booking]').forEach(el => {
      el.hidden = isMasalBooking;
      el.style.display = isMasalBooking ? 'none' : '';
    });
    qsa('[data-masal-external-booking-link]').forEach(el => {
      el.href = bookingHref(l);
      if (isExternalBooking) {
        el.target = '_blank';
        el.rel = 'noopener';
      } else {
        el.removeAttribute('target');
        el.removeAttribute('rel');
      }
    });

    document.title = `${l.name} Appointment | MASAL`;
  }

  function hydrateFlagshipCard() {
    const flagship = locations.find(l => l.handle === config.flagshipHandle) || locations.find(l => String(l.storeType || '').toLowerCase().includes('flagship'));
    if (!flagship) return;
    qsa('[data-masal-flagship-name]').forEach(el => el.textContent = flagship.name || 'MASAL Sydney Flagship');
    qsa('[data-masal-flagship-image]').forEach(el => {
      const customImage = el.dataset.customImage || '';
      const fallback = el.dataset.fallback || '';
      const image = customImage || flagship.image || fallback;
      if (image) el.style.backgroundImage = `url("${image}")`;
    });
    qsa('[data-masal-flagship-cta]').forEach(el => {
      el.href = `${config.appointmentsPath || '/pages/appointments'}?store=${encodeURIComponent(flagship.handle)}`;
    });
  }

  function openRetailerDrawer() {
    const drawer = qs('[data-masal-retailer-drawer]');
    if (!drawer) return;
    drawer.hidden = false;
    document.body.classList.add('masal-appt-drawer-open');
    const input = qs('[data-masal-retailer-search]', drawer);
    setTimeout(() => input?.focus(), 40);
  }

  function closeRetailerDrawer() {
    const drawer = qs('[data-masal-retailer-drawer]');
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('masal-appt-drawer-open');
  }

  function renderRetailers(query = '') {
    const list = qs('[data-masal-retailer-list]');
    if (!list) return;
    const q = query.trim().toLowerCase();
    const retailers = locations
      .filter(l => !String(l.storeType || '').toLowerCase().includes('flagship'))
      .filter(l => !q || [l.name,l.city,l.state,l.postcode,l.country].join(' ').toLowerCase().includes(q))
      .sort((a,b) => (Number(a.priority || 999) - Number(b.priority || 999)) || String(a.name).localeCompare(String(b.name)));

    if (!retailers.length) {
      list.innerHTML = `<div class="masal-appt-retailer-empty">No MASAL appointment retailers match that search.</div>`;
      return;
    }

    list.innerHTML = retailers.map(l => {
      const href = bookingHref(l);
      const external = String(l.bookingType || '').toLowerCase().includes('external');
      return `<article class="masal-appt-retailer-item">
        <div>
          <strong>${esc(l.name)}</strong>
          <small>${esc([l.city,l.state,l.postcode].filter(Boolean).join(', '))}</small>
        </div>
        <a class="masal-appt-button" href="${esc(href)}" ${external ? 'target="_blank" rel="noopener"' : ''}>Book appointment</a>
      </article>`;
    }).join('');
  }

  hydrateFlagshipCard();
  if (selected) {
    populateSelectedStore(selected);
    scheduleBookeoLoad();
  }
  renderRetailers();

  qsa('[data-masal-open-retailers]').forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); openRetailerDrawer(); }));
  qsa('[data-masal-close-retailers]').forEach(btn => btn.addEventListener('click', closeRetailerDrawer));
  qs('[data-masal-retailer-search]')?.addEventListener('input', e => renderRetailers(e.target.value));
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeRetailerDrawer(); });

  // Ensure legacy internal links point to the canonical appointment page.
  qsa('a[href*="/pages/book-appointment"]').forEach(a => {
    try {
      const u = new URL(a.href, window.location.origin);
      if (u.pathname === '/pages/book-appointment') {
        u.pathname = config.appointmentsPath || '/pages/appointments';
        a.href = u.pathname + u.search + u.hash;
      }
    } catch (_) {}
  });
})();
